// src/components/Randevular.js
// Takvim - Randevular ve İş Emri Teslimatları (popover ile detay, tıkla-oluştur, düzenle)
//
// Faz 3 notu: eski sürüm appointments'ı `where("durum","in",[...])` ile çekiyordu.
// Durum alanı olmayan v1 kayıtları (canlıda 72 adet) ve legacy 'Iptal'/'Geldi'
// yazımları bu filtrenin dışında kalıp takvimden tamamen kayboluyordu.
// Artık sorgu GÖRÜNEN TARİH ARALIĞINA göre yapılır, durum süzmesi client-side'dır
// ve normalize edilmiş durum üzerinden çalışır ('İptal' hariç her şey çizilir).

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, query, where, Timestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { Box, Paper, CircularProgress, Typography, useTheme, useMediaQuery, Chip, Stack, Popover, Button, Divider, Tooltip } from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import trLocale from '@fullcalendar/core/locales/tr';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import GeneralRandevuFormModal from './GeneralRandevuFormModal';
import { getRandevuStart, getRandevuEnd, isIptalRandevu, normalizeRandevuDurum } from './AppointmentConstants';

// Renk sabitleri. Randevu yeşili ile iş emri rengi eskiden neredeyse aynıydı
// (#2ecc71 / #27ae60) — iki olay birbirinden ayırt edilemiyordu. İş emri artık
// ayrı bir renk ailesinde (mor-mavi) ve her olayın ikinci sinyali olarak
// başlık öneki + ikon taşıyor (renk körlüğü güvenliği).
const COLORS = {
    aracAlim: '#F2A20C',      // Amber - Randevu / araç alım
    teslim: '#1E8E4E',        // Yeşil - Randevu / araç teslim
    geciken: '#D32F2F',       // Kırmızı - Geciken randevu
    tamamlandi: '#78909C',    // Gri - Tamamlanmış randevu
    isEmriTeslim: '#4A47C9'   // Mor-mavi - İş emri teslimatı
};

const ICONS = {
    alim: '⬇',
    teslim: '⬆',
    isEmri: '▣'
};

// Takvimin görünen aralığının dışına taşan çok günlü kayıtlar da yakalansın diye pay.
const ARALIK_PAYI_GUN = 45;

const getLogoPath = (markaModel) => {
    if (!markaModel || typeof markaModel !== 'string' || markaModel.trim().length === 0) {
        return '/logos/default.svg';
    }
    const logoName = markaModel.split(' ')[0].toLowerCase().replace(' ', '-');
    return `/logos/${logoName}.svg`;
};

const formatTarih = (dateObj) => {
    if (!dateObj) return '-';
    try {
        const d = dateObj.toDate ? dateObj.toDate() : new Date(dateObj);
        return format(d, 'dd MMM yyyy HH:mm', { locale: tr });
    } catch { return '-'; }
};

const Randevular = () => {
    const [allWorkOrders, setAllWorkOrders] = useState([]);
    const [appointmentsByStart, setAppointmentsByStart] = useState([]);
    const [legacyAppointments, setLegacyAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [range, setRange] = useState(null);
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    // Popover state
    const [popoverAnchor, setPopoverAnchor] = useState(null);
    const [popoverData, setPopoverData] = useState(null);

    // Randevu oluştur/düzenle modalı
    const [modalOpen, setModalOpen] = useState(false);
    const [modalInitialData, setModalInitialData] = useState(null);
    const [modalDateRange, setModalDateRange] = useState(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    // İş emirleri: garajdaki aktif işlerin teslim tarihleri
    useEffect(() => {
        const activeStatuses = ["İşlemde", "Tamamlandı", "Teslime Hazır"];
        const qWorkOrders = query(collection(db, "workOrders"), where("durum", "in", activeStatuses));
        const unsub = onSnapshot(qWorkOrders, (snapshot) => {
            const orders = snapshot.docs
                .map(doc => ({ ...doc.data(), id: doc.id, type: 'workOrder' }))
                .filter(order => order.teslimTarihi);
            setAllWorkOrders(orders);
        }, (error) => console.error("İş emri verisi çekilemedi:", error));
        return () => unsub();
    }, []);

    // Randevular: görünen tarih aralığına göre iki sorgu.
    // 1) 'start' alanı olan kayıtlar (yeni şema)
    // 2) yalnızca 'randevuTarihi' taşıyan v1 kayıtları (okuma tarafı fallback)
    useEffect(() => {
        if (!range) return;
        setLoading(true);

        const pay = ARALIK_PAYI_GUN * 24 * 60 * 60 * 1000;
        const from = Timestamp.fromDate(new Date(range.startMs - pay));
        const to = Timestamp.fromDate(new Date(range.endMs + pay));

        const unsubStart = onSnapshot(
            query(collection(db, "appointments"), where("start", ">=", from), where("start", "<=", to)),
            (snapshot) => {
                setAppointmentsByStart(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
                setLoading(false);
            },
            (error) => { console.error("Randevu verisi çekilemedi:", error); setLoading(false); }
        );

        const unsubLegacy = onSnapshot(
            query(collection(db, "appointments"), where("randevuTarihi", ">=", from), where("randevuTarihi", "<=", to)),
            (snapshot) => setLegacyAppointments(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }))),
            (error) => console.error("Legacy randevu verisi çekilemedi:", error)
        );

        return () => { unsubStart(); unsubLegacy(); };
    }, [range]);

    const handleDatesSet = useCallback((arg) => {
        const startMs = arg.start.getTime();
        const endMs = arg.end.getTime();
        setRange(prev => (prev && prev.startMs === startMs && prev.endMs === endMs) ? prev : { startMs, endMs });
    }, []);

    // İki sorgunun sonucu tekilleştirilir; 'İptal' randevular takvimde gizli kalır
    // (K7: bunlara Randevu Listesi ekranındaki İptal sekmesinden erişilir).
    const appointments = useMemo(() => {
        const map = new Map();
        [...appointmentsByStart, ...legacyAppointments].forEach(app => map.set(app.id, app));
        return [...map.values()]
            .map(app => ({ ...app, durum: normalizeRandevuDurum(app.durum) }))
            .filter(app => !isIptalRandevu(app.durum));
    }, [appointmentsByStart, legacyAppointments]);

    const calendarEvents = useMemo(() => {
        const events = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // İş Emri Teslimatları
        allWorkOrders.forEach(wo => {
            const teslimTarihi = wo.teslimTarihi.toDate();
            const marka = wo.aracMarkaModel ? wo.aracMarkaModel.split(' ')[0] : '';
            events.push({
                id: `wo-${wo.id}`,
                title: `İŞ EMRİ TESLİM: ${marka} ${wo.aracPlaka || ''} - ${wo.musteriAdSoyad || ''}`,
                start: teslimTarihi,
                allDay: true,
                backgroundColor: COLORS.isEmriTeslim,
                borderColor: '#2E2B9E',
                textColor: '#fff',
                extendedProps: { ...wo, id: wo.id, type: 'workOrder', eventType: 'isEmri' }
            });
        });

        // Randevular
        appointments.forEach(app => {
            const startDate = getRandevuStart(app);
            const endDate = getRandevuEnd(app);
            if (!startDate) return; // tarihsiz kayıt takvime çizilemez -> Randevu Listesi'nde görünür

            const isTamamlandi = app.durum === 'Tamamlandı';
            const isGeciken = endDate && endDate < today && !isTamamlandi;
            // Appointment'ta aracMarkaModel yok, aracMarka + aracModel var
            const aracMarkaModel = [app.aracMarka, app.aracModel].filter(Boolean).join(' ') || app.aracMarkaModel || '';
            const marka = aracMarkaModel ? aracMarkaModel.split(' ')[0] : '';
            const musteriAd = app.customerName || app.musteriAdSoyad || '';
            const ortakProps = { ...app, id: app.id, type: 'appointment', musteriAdSoyad: musteriAd, aracMarkaModel };

            // ARAÇ ALIM
            events.push({
                id: `app-start-${app.id}`,
                title: `RANDEVU ALIM: ${marka} ${app.aracPlaka || 'Plaka Yok'} - ${musteriAd}`,
                start: startDate,
                allDay: true,
                backgroundColor: isTamamlandi ? COLORS.tamamlandi : COLORS.aracAlim,
                borderColor: isTamamlandi ? COLORS.tamamlandi : '#C07C00',
                textColor: isTamamlandi ? '#fff' : '#000',
                extendedProps: { ...ortakProps, eventType: 'alim' }
            });

            // ARAÇ TESLİM
            if (endDate) {
                events.push({
                    id: `app-end-${app.id}`,
                    title: `RANDEVU TESLİM: ${marka} ${app.aracPlaka || 'Plaka Yok'} - ${musteriAd}`,
                    start: endDate,
                    allDay: true,
                    backgroundColor: isGeciken ? COLORS.geciken : (isTamamlandi ? COLORS.tamamlandi : COLORS.teslim),
                    borderColor: isGeciken ? '#9E1B1B' : (isTamamlandi ? COLORS.tamamlandi : '#136B39'),
                    textColor: '#fff',
                    extendedProps: { ...ortakProps, eventType: 'teslim' }
                });
            }
        });

        return events;
    }, [allWorkOrders, appointments]);

    const handleEventClick = (clickInfo) => {
        clickInfo.jsEvent.preventDefault();
        setPopoverAnchor(clickInfo.el);
        setPopoverData(clickInfo.event.extendedProps);
    };

    const handlePopoverClose = () => {
        setPopoverAnchor(null);
        setPopoverData(null);
    };

    // Boş güne tıkla -> yeni randevu
    const handleDateSelect = (selectInfo) => {
        const start = new Date(selectInfo.start);
        if (selectInfo.allDay) start.setHours(9, 0, 0, 0);
        const end = new Date(start);
        end.setHours(start.getHours() + 1);
        setModalInitialData(null);
        setModalDateRange({ start, end });
        setModalOpen(true);
        selectInfo.view.calendar.unselect();
    };

    const handleEditFromPopover = () => {
        if (!popoverData || popoverData.type !== 'appointment') return;
        setModalDateRange(null);
        setModalInitialData(popoverData);
        setModalOpen(true);
        handlePopoverClose();
    };

    const handleNavigate = () => {
        if (!popoverData) return;
        if (popoverData.type === 'workOrder') {
            navigate(`/is-emri/${popoverData.id}`);
        } else {
            navigate(`/randevu/${popoverData.id}`);
        }
        handlePopoverClose();
    };

    // Çapraz link: randevudan bağlı iş emrine / iş emrinden bağlı randevuya
    const caprazLink = popoverData?.type === 'appointment'
        ? (popoverData.workOrderId ? { to: `/is-emri/${popoverData.workOrderId}`, label: 'Bağlı İş Emrine Git' } : null)
        : (popoverData?.appointmentId ? { to: `/randevu/${popoverData.appointmentId}`, label: 'Bağlı Randevuya Git' } : null);

    const getHizmetBasliklari = (data) => {
        if (!data?.uygulamaTurleri) return [];
        const labels = {
            camFilmi: 'Cam Filmi',
            ppf: 'PPF Kaplama',
            seramik: 'Seramik Kaplama',
            yikama: 'Yıkama',
            diger: 'Diğer Hizmet'
        };
        return (data.uygulamaTurleri || []).map(t => labels[t] || t);
    };

    // Custom event content - tür ikonu + marka logosu
    const renderEventContent = (eventInfo) => {
        const props = eventInfo.event.extendedProps;
        const logoSrc = getLogoPath(props.aracMarkaModel);
        const ikon = ICONS[props.eventType] || ICONS.alim;

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden', width: '100%', px: 0.5 }}>
                <Box component="span" aria-hidden="true" sx={{ fontSize: '0.75rem', lineHeight: 1, flexShrink: 0, color: 'inherit' }}>{ikon}</Box>
                <Box component="img" src={logoSrc} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    sx={{ width: 16, height: 16, objectFit: 'contain', flexShrink: 0, filter: eventInfo.event.textColor === '#fff' ? 'brightness(10)' : 'none' }} />
                <Typography variant="caption" noWrap sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'inherit' }}>
                    {eventInfo.event.title}
                </Typography>
            </Box>
        );
    };

    return (
        <Box>
            {/* Mobilde (listWeek görünümü) uzun olay başlıkları tabloyu ekrandan taşırıyordu:
                375px'te sayfa 487px'e genişliyor ve yatay kaydırma çıkıyordu (Faz 8 responsive testi).
                Tablo sabit yerleşime alınıp başlık hücresi kısıtlanıyor — içerik popover'da tam görünür. */}
            <Paper sx={{
                p: { xs: 1, sm: 2 },
                overflowX: 'hidden',
                '& .fc-list-table': { width: '100%', tableLayout: 'fixed' },
                '& .fc-list-event-title': { maxWidth: 0, overflow: 'hidden' },
                '& .fc-list-day-cushion': { overflow: 'hidden' },
                '& .fc-toolbar-title': { fontSize: { xs: '1rem', sm: '1.25rem' } },
            }}>
                {/* Header ve Legend */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
                        Randevu ve Teslimat Takvimi
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ gap: 0.5, alignItems: 'center' }}>
                        <Chip label={`${ICONS.alim} Randevu Alım`} sx={{ bgcolor: COLORS.aracAlim, color: '#000' }} size="small" />
                        <Chip label={`${ICONS.teslim} Randevu Teslim`} sx={{ bgcolor: COLORS.teslim, color: '#fff' }} size="small" />
                        <Chip label={`${ICONS.teslim} Geciken`} sx={{ bgcolor: COLORS.geciken, color: '#fff' }} size="small" />
                        <Chip label={`${ICONS.isEmri} İş Emri Teslim`} sx={{ bgcolor: COLORS.isEmriTeslim, color: '#fff' }} size="small" />
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<FormatListBulletedIcon />}
                            component={RouterLink}
                            to="/randevular/liste"
                        >
                            Randevu Listesi
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={() => { setModalInitialData(null); setModalDateRange(null); setModalOpen(true); }}
                        >
                            Yeni Randevu
                        </Button>
                    </Stack>
                </Box>

                {loading && <CircularProgress size={22} sx={{ display: 'block', mx: 'auto', mb: 1 }} />}

                <FullCalendar
                    key={isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop'}
                    plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
                    headerToolbar={isMobile ? {
                        left: 'prev,next',
                        center: 'title',
                        right: 'listWeek,dayGridMonth'
                    } : isTablet ? {
                        left: 'prev,next today',
                        center: 'title',
                        right: 'listWeek,dayGridMonth'
                    } : {
                        left: 'prev,next today',
                        center: 'title',
                        right: 'dayGridMonth,timeGridWeek,listWeek'
                    }}
                    initialView={isMobile ? "listWeek" : "dayGridMonth"}
                    locale={trLocale}
                    weekends={true}
                    events={calendarEvents}
                    datesSet={handleDatesSet}
                    selectable={true}
                    select={handleDateSelect}
                    dayMaxEvents={isMobile ? 3 : 5}
                    eventClick={handleEventClick}
                    eventContent={renderEventContent}
                    height={isMobile ? "70vh" : "80vh"}
                    contentHeight="auto"
                    stickyHeaderDates={true}
                    eventDisplay="block"
                    displayEventEnd={false}
                    eventTimeFormat={{
                        hour: '2-digit',
                        minute: '2-digit',
                        meridiem: false
                    }}
                />

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Boş bir güne tıklayarak yeni randevu oluşturabilirsiniz. İptal edilen randevular takvimde gösterilmez;
                    Randevu Listesi ekranındaki İptal sekmesinden erişilir.
                </Typography>
            </Paper>

            {/* Event Detay Popup */}
            <Popover
                open={Boolean(popoverAnchor)}
                anchorEl={popoverAnchor}
                onClose={handlePopoverClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                {popoverData && (
                    <Box sx={{ p: 2, minWidth: 280, maxWidth: 350 }}>
                        <Chip
                            size="small"
                            label={popoverData.type === 'workOrder' ? 'İŞ EMRİ TESLİMATI' : 'RANDEVU'}
                            sx={{
                                mb: 1.5,
                                bgcolor: popoverData.type === 'workOrder' ? COLORS.isEmriTeslim : COLORS.aracAlim,
                                color: popoverData.type === 'workOrder' ? '#fff' : '#000',
                                fontWeight: 700
                            }}
                        />

                        {/* Araç Bilgisi */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                            <Box sx={{ width: 40, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', borderRadius: 1, p: 0.5 }}>
                                <Box component="img" src={getLogoPath(popoverData.aracMarkaModel)} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold">{popoverData.aracPlaka || 'Plaka Yok'}</Typography>
                                <Typography variant="caption" color="text.secondary">{popoverData.aracMarkaModel}</Typography>
                            </Box>
                        </Box>

                        <Divider sx={{ mb: 1.5 }} />

                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Müşteri:</strong> {popoverData.musteriAdSoyad || '-'}
                        </Typography>

                        {popoverData.type === 'appointment' && (
                            <>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Durum:</strong> {normalizeRandevuDurum(popoverData.durum)}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Teslim Alım:</strong> {formatTarih(popoverData.start || popoverData.randevuTarihi)}
                                </Typography>
                                {popoverData.end && (
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                        <strong>Teslim Tarihi:</strong> {formatTarih(popoverData.end)}
                                    </Typography>
                                )}
                            </>
                        )}

                        {popoverData.type === 'workOrder' && popoverData.teslimTarihi && (
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                <strong>Teslim Tarihi:</strong> {formatTarih(popoverData.teslimTarihi)}
                            </Typography>
                        )}

                        {/* Hizmetler */}
                        {popoverData.uygulamaTurleri && popoverData.uygulamaTurleri.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Hizmetler:</Typography>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {getHizmetBasliklari(popoverData).map((h, i) => (
                                        <Chip key={i} label={h} size="small" variant="outlined" />
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        <Divider sx={{ my: 1.5 }} />

                        <Stack spacing={1}>
                            <Button
                                variant="contained"
                                size="small"
                                fullWidth
                                startIcon={<OpenInNewIcon />}
                                onClick={handleNavigate}
                            >
                                {popoverData.type === 'workOrder' ? 'İş Emri Detayı' : 'Randevu Detayı'}
                            </Button>

                            {caprazLink && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    component={RouterLink}
                                    to={caprazLink.to}
                                    onClick={handlePopoverClose}
                                >
                                    {caprazLink.label}
                                </Button>
                            )}

                            {popoverData.type === 'appointment' && (
                                <Tooltip title="Randevu bilgilerini düzenle">
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        startIcon={<EditIcon />}
                                        onClick={handleEditFromPopover}
                                    >
                                        Düzenle
                                    </Button>
                                </Tooltip>
                            )}
                        </Stack>
                    </Box>
                )}
            </Popover>

            <GeneralRandevuFormModal
                isOpen={modalOpen}
                onClose={() => { setModalOpen(false); setModalInitialData(null); setModalDateRange(null); }}
                initialData={modalInitialData}
                dateRange={modalDateRange}
                userRole={currentUser?.role}
            />
        </Box>
    );
};

export default Randevular;
